import { useEffect, useMemo, useRef } from "react";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  gameQuery,
  scoreboardQuery,
  type ScoreboardCell,
  type ScoreboardPlayer,
  type ScoreboardRound,
} from "@/lib/queries";
import { useGameEvents } from "@/lib/sse";
import { buttonStyles } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/cn";

interface ScoreboardSearch {
  finished?: number;
}

export const Route = createFileRoute("/games/$id/scoreboard")({
  component: ScoreboardPage,
  // No auth guard: viewing surface; mutations gated server-side.
  loader: async ({ context, params }) => {
    const [game, scoreboard] = await Promise.all([
      context.queryClient.ensureQueryData(gameQuery(params.id)),
      context.queryClient.ensureQueryData(scoreboardQuery(params.id)),
    ]);
    return { game, scoreboard };
  },
  validateSearch: (raw: Record<string, unknown>): ScoreboardSearch => {
    const f = raw.finished;
    return {
      finished: typeof f === "number" || typeof f === "string" ? Number(f) || undefined : undefined,
    };
  },
});

function leaderId(players: ScoreboardPlayer[]): string | null {
  if (players.length === 0) return null;
  let best = players[0];
  for (const p of players.slice(1)) {
    if (p.total < best.total) best = p;
  }
  // If everyone is tied, don't pick a leader.
  if (players.every((p) => p.total === best.total)) return null;
  return best.id;
}

function ScoreboardPage() {
  const { id } = Route.useParams();
  const { finished } = Route.useSearch();

  useGameEvents(id);

  const gameQ = useQuery(gameQuery(id));
  const sbQ = useQuery(scoreboardQuery(id));

  const sb = sbQ.data;
  const game = gameQ.data;

  const currentRowRef = useRef<HTMLTableRowElement | null>(null);
  const currentRound = sb?.currentRound;

  // Auto-scroll the current round into view on first paint after data lands.
  useEffect(() => {
    const el = currentRowRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "center", inline: "nearest" });
  }, [currentRound, sbQ.dataUpdatedAt]);

  const winnerId = useMemo(
    () => (sb ? leaderId(sb.players) : null),
    [sb],
  );

  if (!sb || !game) return null;

  const isFinished = game.status === "finished" || finished === 1;

  return (
    <section className="mx-auto mt-4 max-w-5xl space-y-5 px-2 pb-6">
      <header className="flex items-center justify-between gap-3 px-1">
        <div>
          <h1 className="font-display text-3xl text-locomotive-900">
            Scoreboard
          </h1>
          <p className="font-mono text-xs text-muted-foreground">
            {sb.rounds.length}-stop run · stop {sb.currentRound} of{" "}
            {sb.rounds.length}
            {game.rules.doubleBlankPenalty
              ? " · double-blank = 50"
              : ""}
            {isFinished ? " · finished" : ""}
          </p>
        </div>
        {!isFinished && (
          <Link
            to="/games/$id/round/$n"
            params={{ id, n: String(sb.currentRound) }}
            className={buttonStyles("ghost", "sm")}
          >
            Back to stop
          </Link>
        )}
      </header>

      {isFinished && (
        <Link
          to="/games/$id/finished"
          params={{ id }}
          className="block rounded-lg border border-ochre-500/60 bg-ochre-500/15 px-4 py-3 font-display text-lg text-ochre-600 hover:bg-ochre-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          🏆 The run's over — see the recap →
        </Link>
      )}

      <Matrix
        gameId={id}
        sb={sb}
        winnerId={winnerId}
        currentRowRef={currentRowRef}
      />
    </section>
  );
}

interface MatrixProps {
  gameId: string;
  sb: {
    players: ScoreboardPlayer[];
    rounds: ScoreboardRound[];
    currentRound: number;
  };
  winnerId: string | null;
  currentRowRef: React.RefObject<HTMLTableRowElement | null>;
}

function Matrix({ gameId, sb, winnerId, currentRowRef }: MatrixProps) {
  return (
    <Card className="p-0">
      {/* Outer scroll container — both axes scroll inside the card. */}
      <div className="max-h-[calc(100dvh-12rem)] overflow-auto rounded-lg">
        <table className="w-full border-collapse text-center font-mono text-sm">
          <thead>
            <tr>
              {/* Top-left corner: empty, sticky on both axes */}
              <th className="sticky left-0 top-0 z-30 min-w-[3.5rem] border-b border-r border-border/50 bg-card px-2 py-2">
                <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  stop
                </span>
              </th>
              {sb.players.map((p) => {
                const isLeader = p.id === winnerId;
                return (
                  <th
                    key={p.id}
                    className={cn(
                      "sticky top-0 z-20 min-w-[5rem] border-b border-border/50 bg-card px-3 py-2",
                      isLeader && "bg-ochre-500/15",
                    )}
                  >
                    <div
                      className={cn(
                        "font-display text-base text-foreground",
                        isLeader && "text-ochre-600",
                      )}
                    >
                      {p.name}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {p.isHost ? "🎩 host" : " "}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sb.rounds.map((round) => {
              const cells = new Map(
                round.cells.map((c): [string, ScoreboardCell] => [c.playerId, c]),
              );
              const isCurrent = round.status === "current";
              return (
                <tr
                  key={round.number}
                  ref={isCurrent ? currentRowRef : null}
                  className={cn(
                    "transition-colors",
                    isCurrent && "bg-rust-500/5",
                  )}
                >
                  <th
                    className={cn(
                      "sticky left-0 z-10 border-b border-r border-border/50 bg-card px-2 py-2 text-left",
                      isCurrent && "bg-rust-500/10",
                    )}
                  >
                    <div className="font-display text-base text-foreground">
                      {round.number}
                    </div>
                  </th>
                  {sb.players.map((p) => {
                    const cell = cells.get(p.id);
                    return (
                      <CellView
                        key={p.id}
                        gameId={gameId}
                        cell={cell}
                        roundStatus={round.status}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <th
                className="sticky bottom-0 left-0 z-30 border-t border-r border-border/50 bg-card px-2 py-2 text-left"
              >
                <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  total
                </span>
              </th>
              {sb.players.map((p) => {
                const isLeader = p.id === winnerId;
                return (
                  <td
                    key={p.id}
                    className={cn(
                      "sticky bottom-0 z-20 border-t border-border/50 bg-card px-3 py-2",
                      isLeader && "bg-ochre-500/15",
                    )}
                  >
                    <div
                      className={cn(
                        "font-display text-2xl",
                        isLeader ? "text-ochre-600" : "text-foreground",
                      )}
                    >
                      {p.total}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

interface CellViewProps {
  gameId: string;
  cell: ScoreboardCell | undefined;
  roundStatus: "current" | "past" | "future";
}

function CellView({ gameId, cell, roundStatus }: CellViewProps) {
  if (!cell || roundStatus === "future") {
    return <td className="border-b border-border/30 px-3 py-2 text-muted-foreground/40" />;
  }

  // Past, not submitted → dim em-dash.
  if (roundStatus === "past" && !cell.submitted) {
    return (
      <td className="border-b border-border/30 px-3 py-2 text-muted-foreground/60">
        —
      </td>
    );
  }

  // Current, not submitted → empty placeholder dot.
  if (roundStatus === "current" && !cell.submitted) {
    return (
      <td className="border-b border-border/30 px-3 py-2 text-muted-foreground/60">
        ·
      </td>
    );
  }

  // Submitted (past or current). Tap → score detail / audit page.
  const inner = (
    <span
      className={cn(
        "inline-flex items-baseline gap-1",
        roundStatus === "current" && "border-b-2 border-rust-500",
      )}
    >
      <span className="font-mono text-base text-foreground">
        {cell.total ?? 0}
      </span>
      {cell.audited && (
        <span
          aria-label="audited"
          title="audited"
          className="font-mono text-[10px] text-rust-600"
        >
          *
        </span>
      )}
      {cell.hasPhoto && (
        <span aria-label="has photo" title="has photo" className="text-[10px]">
          📷
        </span>
      )}
    </span>
  );

  return (
    <td
      className={cn(
        "border-b border-border/30 px-3 py-2",
        roundStatus === "current" && "bg-rust-500/5",
      )}
    >
      {cell.scoreId ? (
        <Link
          to="/games/$id/scores/$scoreId/audit"
          params={{ id: gameId, scoreId: cell.scoreId }}
          className="rounded px-1 hover:bg-sand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {inner}
        </Link>
      ) : (
        inner
      )}
    </td>
  );
}

